import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DotVRIcon from "../../icons/DotVRIcon";
import Badge from "../../components/Badge";
import Button from "../../components/Button";

type SortableGraphDataProps = {
  snapshot: any;
  onDelete: any;
  onUpdate: any;
};

const SortableGraphData: React.FC<SortableGraphDataProps> = ({
  snapshot,
  onDelete,
  onUpdate,
}): React.ReactElement => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(snapshot.graph_name);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: snapshot.id, data: { snapshot } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleEditClick = () => {
    if (isEditing) {
      onUpdate(snapshot.id, editedName);
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(snapshot.graph_name);
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleEditClick();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (
      relatedTarget &&
      (relatedTarget.textContent === "Save" ||
        relatedTarget.textContent === "Cancel")
    ) {
      return;
    }
    handleCancelEdit();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      key={snapshot.id}
      className="flex items-center bg-gray-300 border-b-2 px-2 gap-2"
    >
      <DotVRIcon
        {...attributes}
        {...listeners}
        className="w-4 h-4 mr-2 cursor-move"
      />
      <div className="cursor-pointer p-4 flex-shrink-0 min-w-0">
        {isEditing ? (
          <input
            type="text"
            value={editedName}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            className="border border-gray-400 rounded px-2 py-1 text-sm w-full min-w-[150px]"
            autoFocus
            onBlur={handleBlur} // Cancel if clicked outside (except on buttons)
          />
        ) : (
          <span>{snapshot.graph_name}</span>
        )}
      </div>
      <div className="grow">
        {snapshot.graph_data &&
          snapshot.graph_data.selectedMetrics &&
          snapshot.graph_data.selectedMetrics.length &&
          snapshot.graph_data.selectedMetrics.map(
            (metric: any, index: number) => {
              return (
                <Badge key={index} status="info">
                  {metric.name}
                </Badge>
              );
            }
          )}
      </div>
      {snapshot.graph_data && (
        <>
          <Badge status="info">{snapshot.graph_data.period}</Badge>
          <Badge status="info">{snapshot.graph_data.graphType}</Badge>
          <Button variant="info" size="small" onClick={handleEditClick}>
            {isEditing ? "Save" : "Edit"}
          </Button>
          {isEditing && (
            <Button variant="secondary" size="small" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          <Button
            variant="error"
            size="small"
            onClick={() => onDelete(snapshot.graph_name)}
          >
            Delete
          </Button>
        </>
      )}
    </div>
  );
};

export default SortableGraphData;
